import React, { useState, useRef, useEffect, useMemo } from "react";
import { Mic, Square, Upload, Loader2, Play, Pause, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

function encodeWav(buffer, startSec, endSec) {
  const sr = buffer.sampleRate;
  const ch = buffer.numberOfChannels;
  const s = Math.max(0, Math.floor(startSec * sr));
  const e = Math.min(buffer.length, Math.floor(endSec * sr));
  const len = Math.max(0, e - s);
  const chans = [];
  for (let c = 0; c < ch; c++) chans.push(buffer.getChannelData(c));
  const out = new ArrayBuffer(44 + len * ch * 2);
  const view = new DataView(out);
  const wr = (o, str) => { for (let i = 0; i < str.length; i++) view.setUint8(o + i, str.charCodeAt(i)); };
  wr(0, "RIFF"); view.setUint32(4, 36 + len * ch * 2, true); wr(8, "WAVE"); wr(12, "fmt ");
  view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, ch, true);
  view.setUint32(24, sr, true); view.setUint32(28, sr * ch * 2, true); view.setUint16(32, ch * 2, true);
  view.setUint16(34, 16, true); wr(36, "data"); view.setUint32(40, len * ch * 2, true);
  let off = 44;
  for (let i = s; i < e; i++) for (let c = 0; c < ch; c++) {
    let v = Math.max(-1, Math.min(1, chans[c][i]));
    view.setInt16(off, v < 0 ? v * 0x8000 : v * 0x7fff, true); off += 2;
  }
  return new Blob([out], { type: "audio/wav" });
}

function peaksFrom(buffer, n = 48) {
  const d = buffer.getChannelData(0);
  const step = Math.floor(d.length / n) || 1;
  const out = [];
  for (let i = 0; i < n; i++) {
    let max = 0;
    for (let j = 0; j < step; j++) { const v = Math.abs(d[i * step + j] || 0); if (v > max) max = v; }
    out.push(0.15 + Math.min(1, max * 1.6) * 0.85);
  }
  return out;
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function AudioComposer({ onReady }) {
  const [file, setFile] = useState(null);
  const [buffer, setBuffer] = useState(null);
  const [dur, setDur] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null); const chunks = useRef([]); const startT = useRef(0); const timer = useRef(null);
  const audioEl = useRef(null); const urlRef = useRef(null);
  const peaks = useMemo(() => (buffer ? peaksFrom(buffer) : []), [buffer]);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const loadFile = async (f) => {
    setBusy(true);
    try {
      const ab = await f.arrayBuffer();
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const buf = await ctx.decodeAudioData(ab.slice(0));
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(f);
      setFile(f); setBuffer(buf); setDur(buf.duration); setStart(0); setEnd(buf.duration);
    } catch (e) { toast("Couldn't read that audio", { description: "Try a different file or record instead." }); }
    finally { setBusy(false); }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      rec.ondataavailable = (ev) => ev.data.size && chunks.current.push(ev.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timer.current) clearInterval(timer.current);
        setRecording(false);
        const blob = new Blob(chunks.current, { type: rec.mimeType || "audio/webm" });
        await loadFile(new File([blob], `voice-${Date.now()}.webm`, { type: blob.type }));
      };
      recRef.current = rec; startT.current = Date.now(); setRecSecs(0); setRecording(true);
      timer.current = setInterval(() => { const s = Math.floor((Date.now() - startT.current) / 1000); setRecSecs(s); if (s >= 180) stopRec(); }, 250);
      rec.start();
    } catch { toast("Microphone unavailable", { description: "Allow mic access or upload a file." }); }
  };
  const stopRec = () => { try { recRef.current?.state === "recording" && recRef.current.stop(); } catch {} };

  const togglePreview = () => {
    const a = audioEl.current; if (!a) return;
    if (a.paused) { a.currentTime = start; a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };

  const useClip = async () => {
    if (!buffer || !file) return;
    setBusy(true);
    try {
      const trimmed = start > 0.05 || end < dur - 0.05;
      const finalFile = trimmed ? new File([encodeWav(buffer, start, end)], `voice-${Date.now()}.wav`, { type: "audio/wav" }) : file;
      const up = await api.uploadMedia(finalFile);
      let transcript = "";
      try { transcript = (await api.transcribe(finalFile)).text || ""; } catch {}
      onReady({ url: up.url, type: "audio", duration: Math.max(1, end - start), transcript });
    } catch (e) { toast("Upload failed", { description: e.message }); }
    finally { setBusy(false); }
  };

  if (!buffer) {
    return (
      <div className="mt-3 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-am-border bg-am-surface2/50 py-6">
        {recording ? (
          <>
            <div className="flex items-center gap-2 text-sm font-semibold text-white"><span className="h-2.5 w-2.5 animate-pulse rounded-full bg-aura-magenta" /> Recording… {fmt(recSecs)}</div>
            <button onClick={stopRec} data-testid="audio-stop-rec" className="flex items-center gap-2 rounded-full aura-gradient px-5 py-2.5 text-sm font-bold text-black"><Square className="h-4 w-4" fill="currentColor" /> Stop</button>
          </>
        ) : (
          <>
            <button onClick={startRec} disabled={busy} data-testid="audio-record" className="grid h-16 w-16 place-items-center rounded-full aura-gradient text-black active:scale-95 disabled:opacity-60">{busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <Mic className="h-7 w-7" />}</button>
            <p className="text-xs text-am-text2">Tap to record a voice post (up to 3 min)</p>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-am-text2 hover:text-white"><Upload className="h-3.5 w-3.5" /> or upload an audio file
              <input type="file" hidden accept="audio/*" data-testid="audio-upload" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) loadFile(f); }} />
            </label>
          </>
        )}
      </div>
    );
  }

  const sPct = dur ? (start / dur) * 100 : 0;
  const ePct = dur ? (end / dur) * 100 : 100;
  return (
    <div className="mt-3 rounded-2xl border border-am-border bg-am-surface2 p-3">
      <audio ref={audioEl} src={urlRef.current} onTimeUpdate={(e) => { if (e.target.currentTime >= end) { e.target.pause(); setPlaying(false); } }} onEnded={() => setPlaying(false)} />
      <div className="flex items-center gap-3">
        <button onClick={togglePreview} data-testid="audio-preview-toggle" className="grid h-10 w-10 shrink-0 place-items-center rounded-full aura-gradient text-black active:scale-90">{playing ? <Pause className="h-4 w-4" fill="currentColor" /> : <Play className="h-4 w-4 translate-x-[1px]" fill="currentColor" />}</button>
        <div className="relative h-10 flex-1">
          <div className="flex h-full items-center gap-[2px]">
            {peaks.map((p, i) => { const inSel = (i / peaks.length) * 100 >= sPct && (i / peaks.length) * 100 <= ePct; return <span key={i} className="flex-1 rounded-full" style={{ height: `${p * 100}%`, background: inSel ? "linear-gradient(180deg,#ff2e93,#ffd23c,#38e1ff)" : "#2a2d33" }} />; })}
          </div>
        </div>
        <span className="shrink-0 font-mono text-xs tabular-nums text-am-text2">{fmt(end - start)}</span>
      </div>
      <div className="mt-3 space-y-2">
        <label className="flex items-center gap-2 text-[11px] text-am-text2">Trim start<input type="range" min={0} max={dur} step={0.1} value={start} data-testid="audio-trim-start" onChange={(e) => setStart(Math.min(parseFloat(e.target.value), end - 0.5))} className="flex-1 accent-aura-magenta" /><span className="w-10 text-right font-mono">{fmt(start)}</span></label>
        <label className="flex items-center gap-2 text-[11px] text-am-text2">Trim end<input type="range" min={0} max={dur} step={0.1} value={end} data-testid="audio-trim-end" onChange={(e) => setEnd(Math.max(parseFloat(e.target.value), start + 0.5))} className="flex-1 accent-aura-cyan" /><span className="w-10 text-right font-mono">{fmt(end)}</span></label>
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        <button onClick={() => { setBuffer(null); setFile(null); }} className="rounded-full px-3 py-2 text-xs font-semibold text-am-text2 hover:text-white"><X className="mr-1 inline h-3.5 w-3.5" />Discard</button>
        <button onClick={useClip} disabled={busy} data-testid="audio-use-clip" className="flex items-center gap-1.5 rounded-full aura-gradient px-4 py-2 text-xs font-bold text-black disabled:opacity-60">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}{busy ? "Processing…" : "Use clip"}</button>
      </div>
    </div>
  );
}
